import { ErrorHandler } from "../../../shared/utils/errorHandler";

export const CHANNELS = ["SMS", "WHATSAPP"];
export const SUBCATEGORIES = [
  "FEE_DUE_REMINDER", "FEE_PAID_RECEIPT", "FEE_OVERDUE_ALERT", "FEE_CONCESSION_GRANTED",
  "STUDENT_ABSENT_ALERT", "STUDENT_LATE_ALERT", "STAFF_ABSENT_ALERT", "LOW_ATTENDANCE_WARNING",
  "EXAM_SCHEDULE_PUBLISHED", "RESULT_PUBLISHED", "GRADE_UPDATED", "HOMEWORK_ASSIGNED",
  "EVENT_REMINDER", "HOLIDAY_NOTICE", "GENERAL_NOTICE", "CIRCULAR", "ADMISSION_CONFIRMED",
  "ADMISSION_DOCUMENTS_PENDING", "TC_ISSUED", "SALARY_SLIP", "LEAVE_APPROVED", "LEAVE_REJECTED",
  "LOW_SMS_BALANCE", "TRIAL_EXPIRING", "SUBSCRIPTION_RENEWED",
];
export const pretty = (value) => String(value || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
export const formatINR = (value) => Number.isFinite(Number(value))
  ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(value)) : "Not available";
export const messagingError = (error) => {
  const body = error?.response?.data;
  const status = error?.response?.status;
  if (status === 401 || status === 403) return ErrorHandler.getMessage(error).message;
  if (status === 410) return "This messaging operation is no longer supported. Refresh the page or contact support.";
  const fields = body?.errors ?? body?.validationErrors;
  const details = fields && typeof fields === "object" ? Object.entries(fields).map(([key, value]) => `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`).join("; ") : "";
  return [body?.message || ErrorHandler.getMessage(error).message, body?.errorCode, details].filter(Boolean).join(" - ");
};
export const messagingRoles = (user) => (user?.roles || []).map((role) => String(role).toUpperCase().replace(/^ROLE_/, ""));
export const isMessagingAdmin = (user) => messagingRoles(user).some((role) => ["SCHOOL_ADMIN", "PRINCIPAL", "ADMIN", "SUPER_ADMIN"].includes(role));
export const isMessagingSuperAdmin = (user) => messagingRoles(user).includes("SUPER_ADMIN");

export const normalizeSettings = (data) => {
  const preferences = {};
  Object.values(data.grouped || {}).forEach((items) => {
    if (!Array.isArray(items)) throw new Error("Notification preferences response is invalid.");
    items.forEach((item) => {
      if (!SUBCATEGORIES.includes(item.notificationSubCategory)) return;
      preferences[item.notificationSubCategory] = {
        isEnabled: item.isEnabled ?? false, emailEnabled: item.emailEnabled ?? false,
        smsEnabled: item.smsEnabled ?? false, whatsappEnabled: item.whatsappEnabled ?? false,
        daysBefore: item.daysBefore ?? null,
      };
    });
  });
  return { ...data, smsApiKey: "", whatsappApiKey: "", smsMode: data.smsMode || "SCHOOL_OWNED", smsProvider: data.smsProvider || "MSG91",
    whatsappMode: data.whatsappMode || "SCHOOL_OWNED", whatsappProvider: data.whatsappProvider || "META", preferences };
};
export const settingsPatch = (saved, edited) => {
  const keys = ["smsProvider", "smsSenderId", "smsMode", "smsAcceptedUnitPrice", "whatsappAcceptedUnitPrice",
    "smsAccountId", "whatsappMode", "whatsappProvider", "whatsappAccountId", "whatsappSender",
    "whatsappPhoneNumberId", "whatsappBusinessAccountId", "studentCheckOutEnabled", "manualFeeCollectionOnly"];
  const patch = {};
  keys.forEach((key) => { if (edited[key] !== saved[key]) patch[key] = edited[key]; });
  ["smsApiKey", "whatsappApiKey"].forEach((key) => { if (edited[key]?.trim()) patch[key] = edited[key].trim(); });
  const preferences = {};
  Object.entries(edited.preferences || {}).forEach(([key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify(saved.preferences?.[key])) preferences[key] = value;
  });
  if (Object.keys(preferences).length) patch.preferences = preferences;
  return patch;
};
export const deliveryLabel = (status) => ({
  PENDING: "Queued", PROCESSING: "Sending / charge reserved", ACCEPTED: "Provider accepted - delivery not confirmed",
  FAILED: "Failed / rejected", BLOCKED: "Not sent - action required",
  UNKNOWN: "Acceptance uncertain - support reconciliation required",
}[status] || `Unrecognized status: ${status}`);
export const readMessagingPage = (data) => {
  const metadata = data?.page ?? data;
  if (!Array.isArray(data?.content) || !Number.isInteger(metadata?.totalPages) || metadata.totalPages < 0
    || !Number.isInteger(metadata?.number) || metadata.number < 0) {
    throw new Error("The messaging service returned an invalid page.");
  }
  return { ...data, ...metadata, content: data.content };
};
export const parseVariableKeys = (value) => {
  let keys;
  try { keys = typeof value === "string" ? JSON.parse(value) : value; }
  catch { throw new Error("Template variableKeys is not valid JSON."); }
  if (!Array.isArray(keys) || keys.length > 30 || keys.some((key) => typeof key !== "string" || !/^[A-Z][A-Z0-9_]{0,49}$/.test(key)) || new Set(keys).size !== keys.length) {
    throw new Error("Template variables must be up to 30 unique uppercase names in provider order.");
  }
  return keys;
};
export const templateFor = (channel, subCategory, settings, wallet, templates, platformTemplates) => {
  const prefix = channel === "SMS" ? "sms" : "whatsapp";
  const paid = settings[`${prefix}Mode`] === "PLATFORM_PAID";
  const provider = paid ? wallet?.[prefix]?.provider : settings[`${prefix}Provider`];
  return (paid ? platformTemplates : templates).find((item) =>
    item.channel === channel && item.subCategory === subCategory && item.provider === provider && item.approved === true);
};
export const channelProblem = (channel, settings, wallet) => {
  const prefix = channel === "SMS" ? "sms" : "whatsapp";
  if (settings[`${prefix}Mode`] === "PLATFORM_PAID") {
    const readiness = wallet?.[prefix];
    if (!readiness?.ready || !(Number(readiness.unitPrice) > 0)) return readiness?.reason || "Platform service or pricing is not ready.";
    if (settings[`${prefix}AcceptedUnitPrice`] == null || Number(settings[`${prefix}AcceptedUnitPrice`]) !== Number(readiness.unitPrice)) return "Confirm updated pricing in Channels & Providers.";
  } else if (!settings[`${prefix}Configured`]) return "Save valid school-owned provider credentials first.";
  return "";
};
